export interface ErrorHandlerOptions {
    showToast?: boolean;
    customMessage?: string;
}

export interface ErrorInfo {
    title: string;
    message: string;
    variant: 'destructive' | 'warning' | 'info';
}

// Common error handler that returns standardized error information
export const handleApiError = (error: any, options: ErrorHandlerOptions = {}): ErrorInfo => {
    const { customMessage } = options;

    // Default error info
    let errorInfo: ErrorInfo = {
        title: 'Error',
        message: customMessage || 'Something went wrong. Please try again.',
        variant: 'destructive',
    };

    // Handle different error types
    if (error?.status) {
        switch (error.status) {
            case 400:
                errorInfo = {
                    title: 'Invalid Data',
                    message: customMessage || error.message || 'Please check your information and try again.',
                    variant: 'destructive',
                };
                break;

            case 401:
                errorInfo = {
                    title: 'Authentication Required',
                    message: error.message || customMessage || 'Please sign in to continue.',
                    variant: 'warning',
                };
                break;

            case 403:
                errorInfo = {
                    title: 'Access Denied',
                    message: customMessage || 'You do not have permission to perform this action.',
                    variant: 'destructive',
                };
                break;

            case 404:
                errorInfo = {
                    title: 'Not Found',
                    message: customMessage || 'The requested resource was not found.',
                    variant: 'destructive',
                };
                break;

            case 409:
                errorInfo = {
                    title: 'Already Exists',
                    message: customMessage || error.message || 'This resource already exists.',
                    variant: 'warning',
                };
                break;

            case 422:
                errorInfo = {
                    title: 'Validation Error',
                    message: customMessage || error.message || 'Please check your information and try again.',
                    variant: 'destructive',
                };
                break;

            case 429:
                errorInfo = {
                    title: 'Too Many Requests',
                    message: customMessage || 'Please wait a moment before trying again.',
                    variant: 'warning',
                };
                break;

            case 500:
            case 502:
            case 503:
            case 504:
                errorInfo = {
                    title: 'Server Error',
                    message: customMessage || 'Our servers are experiencing issues. Please try again later.',
                    variant: 'destructive',
                };
                break;

            default:
                errorInfo = {
                    title: 'Error',
                    message: customMessage || error.message || 'An unexpected error occurred.',
                    variant: 'destructive',
                };
        }
    } else if (error?.message) {
        // Handle errors without status codes
        errorInfo.message = customMessage || error.message;
    }

    return errorInfo;
};

// Specific error handlers for common scenarios
export const authErrorHandler = (error: any, customMessage?: string): ErrorInfo => {
    if (error?.status === 409) {
        return handleApiError(error, {
            customMessage: customMessage || 'An account with this email already exists. Please try signing in instead.',
        });
    }

    return handleApiError(error, { customMessage });
};

export const networkErrorHandler = (error: any): ErrorInfo => {
    if (!navigator.onLine) {
        return {
            title: 'No Internet Connection',
            message: 'Please check your internet connection and try again.',
            variant: 'warning',
        };
    }

    return handleApiError(error, {
        customMessage: 'Network error. Please check your connection and try again.',
    });
};
